// src/pages/BoneFrame.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Film & Dizi Linki Derleme Merkezi  ·  BoneFrame
// Veri: Firebase Realtime DB /frames
// Özellikler:
//   • Film/dizi linkleri için otomatik platform tespiti (Netflix, Prime vb.)
//   • Tür seçimi: Film / Dizi / Belgesel / Animasyon / Diğer
//   • Platform badge + otomatik URL analizi
//   • Her kart → "Framed by: [DisplayName]" + tarih → /profile/:uid
//   • Gümüş/platin estetik: soğuk gri-beyaz, mavi-gümüş parıltı
//   • Admin: moderasyon paneli + kullanıcı engelleme
//   • BoneBeat ile aynı mimari (authLoading koruması, onError callback)
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState }        from "react";
import { useNavigate }                        from "react-router-dom";
import { useTranslation }                     from "react-i18next";
import { ref, onValue, push, update, remove } from "firebase/database";
import { db }                                 from "../firebase/config";
import { useAuth }                            from "../context/AuthContext";
import CommentSection                         from "../components/CommentSection";
import SidebarFrame                           from "../components/Sidebar/SidebarFrame.jsx";
import { SidebarFrameProvider }               from "../context/SidebarFrameContext.jsx";
import { cleanContentUrl }                    from "../utils/cleanContentUrl";
import { filterText }                          from "../utils/profanityFilter";

// ═══════════════════════════════════════════════════════════════════════════════
// HOOK: uid ile canlı kullanıcı bilgisi — isim/foto değişince otomatik güncellenir
// ═══════════════════════════════════════════════════════════════════════════════
function useUserInfo(uid) {
  const [info, setInfo] = useState({ displayName: null, photoURL: null });
  useEffect(() => {
    if (!uid) return;
    const r = ref(db, `publicProfiles/${uid}`);
    const unsub = onValue(r, snap => {
      if (snap.exists()) {
        const d = snap.val();
        setInfo({ displayName: d.displayName || null, photoURL: d.photoURL || null });
      }
    });
    return () => unsub();
  }, [uid]);
  return info;
}

// ═══════════════════════════════════════════════════════════════════════════════
// RENK PALETİ — Gümüş / Platin Tema
// ═══════════════════════════════════════════════════════════════════════════════

const C = {
  bg:           "#09090f",          // Derin lacivert-siyah
  bgCard:       "#0e0e18",          // Kart arka planı
  bgDeep:       "#060610",          // En koyu
  bgInput:      "#13131f",          // Input arka planı

  border:       "rgba(192,204,224,0.1)",
  borderHov:    "rgba(192,204,224,0.3)",
  borderFaint:  "rgba(192,204,224,0.06)",

  silver:       "#c0cce0",          // Ana gümüş
  silverDim:    "rgba(192,204,224,0.5)",
  silverGhost:  "var(--bone-text-muted)",
  silverFaint:  "rgba(192,204,224,0.07)",

  platinum:     "#e8eef7",          // En açık — başlıklar
  platinumDim:  "rgba(232,238,247,0.6)",
  platinumGhost:"rgba(232,238,247,0.22)",

  // Vurgu: soğuk mavi-gümüş
  accent:       "#7ba7d4",          // Soft mavi
  accentDim:    "rgba(123,167,212,0.5)",
  accentGhost:  "rgba(123,167,212,0.15)",
  accentFaint:  "rgba(123,167,212,0.07)",

  red:          "#e05c6e",
  redDim:       "rgba(224,92,110,0.55)",
  green:        "#6ec9a0",
};

// ═══════════════════════════════════════════════════════════════════════════════
// FILM/DİZİ PLATFORMLARI — Otomatik URL Tespiti
// BoneBeat'teki yaklaşımla aynı: regex → platform adı + ikon + renk
// ═══════════════════════════════════════════════════════════════════════════════

const PLATFORMS = [
  // ── Streaming ──────────────────────────────────────────────────────────────
  { name: "Netflix",        regex: /^https?:\/\/(www\.)?netflix\.com\//,               icon: "◈", color: "#e50914" },
  { name: "Prime Video",    regex: /^https?:\/\/(www\.)?primevideo\.com\//,             icon: "◆", color: "#00a8e0" },
  { name: "Disney+",        regex: /^https?:\/\/(www\.)?disneyplus\.com\//,             icon: "✦", color: "#113ccf" },
  { name: "Apple TV+",      regex: /^https?:\/\/(www\.)?tv\.apple\.com\//,              icon: "◉", color: "#c8c8c8" },
  { name: "HBO Max",        regex: /^https?:\/\/(www\.)?(max|hbomax)\.com\//,           icon: "⬡", color: "#6a5acd" },
  { name: "Hulu",           regex: /^https?:\/\/(www\.)?hulu\.com\//,                   icon: "▶", color: "#1ce783" },
  { name: "Paramount+",     regex: /^https?:\/\/(www\.)?paramountplus\.com\//,          icon: "⬢", color: "#0064ff" },
  { name: "Peacock",        regex: /^https?:\/\/(www\.)?peacocktv\.com\//,              icon: "◎", color: "#f47521" },
  { name: "Mubi",           regex: /^https?:\/\/(www\.)?mubi\.com\//,                  icon: "✏", color: "#ff5a5f" },
  { name: "Criterion",      regex: /^https?:\/\/(www\.)?criterionchannel\.com\//,       icon: "⚑", color: "#e8c84a" },
  { name: "Shudder",        regex: /^https?:\/\/(www\.)?shudder\.com\//,               icon: "◇", color: "#c0392b" },
  { name: "Crunchyroll",    regex: /^https?:\/\/(www\.)?crunchyroll\.com\//,           icon: "◐", color: "#f47521" },
  { name: "Funimation",     regex: /^https?:\/\/(www\.)?funimation\.com\//,            icon: "◑", color: "#410099" },
  { name: "Tubi",           regex: /^https?:\/\/(www\.)?tubitv\.com\//,               icon: "▷", color: "#fa5a00" },
  { name: "Pluto TV",       regex: /^https?:\/\/(www\.)?pluto\.tv\//,                 icon: "◊", color: "#fff200" },
  // ── Yerel / Türkiye ────────────────────────────────────────────────────────
  { name: "BluTV",          regex: /^https?:\/\/(www\.)?blutv\.com\//,                icon: "◈", color: "#0055ff" },
  { name: "MUBI TR",        regex: /^https?:\/\/(www\.)?mubi\.com\/tr\//,             icon: "✏", color: "#ff5a5f" },
  { name: "Gain",           regex: /^https?:\/\/(www\.)?gain\.tv\//,                  icon: "◆", color: "#ff0050" },
  { name: "Tabii",          regex: /^https?:\/\/(www\.)?tabii\.com\//,                icon: "⚡", color: "#c0a040" },
  // ── Referans / Öneri siteleri ───────────────────────────────────────────────
  { name: "Letterboxd",     regex: /^https?:\/\/(www\.)?letterboxd\.com\//,           icon: "◉", color: "#c8c8c8" },
  { name: "IMDb",           regex: /^https?:\/\/(www\.)?imdb\.com\//,                 icon: "⬢", color: "#f5c518" },
  { name: "Rotten Tomatoes",regex: /^https?:\/\/(www\.)?rottentomatoes\.com\//,       icon: "◎", color: "#fa320a" },
  { name: "JustWatch",      regex: /^https?:\/\/(www\.)?justwatch\.com\//,            icon: "▶", color: "#ffd700" },
  { name: "Metacritic",     regex: /^https?:\/\/(www\.)?metacritic\.com\//,           icon: "◐", color: "#ffcc34" },
  { name: "Filmweb",        regex: /^https?:\/\/(www\.)?filmweb\.pl\//,               icon: "✦", color: "#ff6600" },
  { name: "Trakt",          regex: /^https?:\/\/(www\.)?trakt\.tv\//,                 icon: "◇", color: "#ed2224" },
  // ── Video paylaşım ─────────────────────────────────────────────────────────
  { name: "YouTube",        regex: /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\//,  icon: "▶", color: "#ff0000" },
  { name: "Vimeo",          regex: /^https?:\/\/(www\.)?vimeo\.com\//,                icon: "◉", color: "#1ab7ea" },
  { name: "Dailymotion",    regex: /^https?:\/\/(www\.)?dailymotion\.com\//,          icon: "◆", color: "#00b4ff" },
];

// Platform tespiti (otomatik)
function detectPlatform(url = "") {
  return PLATFORMS.find(p => p.regex.test(url)) || null;
}

// İçerik türleri
const CONTENT_TYPES = [
  { key: "film",      label: "Film",       icon: "◈" },
  { key: "dizi",      label: "Dizi",       icon: "▶" },
  { key: "belgesel",  label: "Belgesel",   icon: "◉" },
  { key: "animasyon", label: "Animasyon",  icon: "✦" },
  { key: "diger",     label: "Diğer",      icon: "◆" },
];

// ── Yardımcı fonksiyonlar ──────────────────────────────────────────────────────

function timeAgo(ts) {
  if (!ts) return "";
  const diff = Date.now() - ts, m = Math.floor(diff / 60000);
  if (m < 1)  return "şimdi";
  if (m < 60) return `${m}dk önce`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}sa önce`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}g önce`;
  return new Date(ts).toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" });
}

function fullDate(ts) {
  if (!ts) return "";
  return new Date(ts).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
}

// ═══════════════════════════════════════════════════════════════════════════════
// KÜÇÜK UI BİLEŞENLERİ — Gümüş tema
// ═══════════════════════════════════════════════════════════════════════════════

function IconBtn({ onClick, title, danger, children, disabled }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} title={title} disabled={disabled}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: "none", border: "none", borderRadius: 6, cursor: disabled ? "not-allowed" : "pointer",
        color: danger ? (hov ? C.red : C.redDim) : (hov ? C.silver : C.silverDim),
        padding: "4px 6px", display: "flex", alignItems: "center",
        transition: "color 0.2s", opacity: disabled ? 0.4 : 1,
      }}>
      {children}
    </button>
  );
}

function PrimaryBtn({ onClick, children, disabled, small }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} disabled={disabled}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: disabled ? "rgba(192,204,224,0.1)" : hov ? "#a8b8d0" : C.silver,
        border: `1px solid ${disabled ? C.border : C.silver}`,
        color: disabled ? C.silverDim : C.bg,
        padding: small ? "8px 16px" : "11px 24px",
        borderRadius: 8,
        fontFamily: "'Courier Prime',monospace", fontWeight: "bold",
        fontSize: small ? 10 : 11, letterSpacing: "0.2em", textTransform: "uppercase",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 0.2s", opacity: disabled ? 0.6 : 1,
      }}>
      {children}
    </button>
  );
}

function GhostBtn({ onClick, children, danger }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: "transparent",
        border: `1px solid ${danger ? (hov ? C.red : C.redDim) : (hov ? C.silver : C.border)}`,
        borderRadius: 8,
        color: danger ? (hov ? C.red : C.redDim) : (hov ? C.silver : C.silverDim),
        padding: "8px 16px", fontFamily: "'Courier Prime',monospace",
        fontWeight: "bold", fontSize: 10, letterSpacing: "0.18em",
        textTransform: "uppercase", cursor: "pointer", transition: "all 0.2s",
      }}>
      {children}
    </button>
  );
}

function TextInput({ value, onChange, placeholder, disabled, onKeyDown, type = "text" }) {
  return (
    <input type={type} value={value} onChange={onChange}
      placeholder={placeholder} disabled={disabled} onKeyDown={onKeyDown}
      style={{
        display: "block", width: "100%", boxSizing: "border-box",
        padding: "10px 13px", borderRadius: 6,
        background: disabled ? C.silverFaint : C.bgInput,
        border: `1px solid ${C.border}`,
        color: disabled ? C.silverDim : C.platinum,
        fontFamily: "'Courier Prime',monospace", fontSize: 12,
        outline: "none", letterSpacing: "0.04em",
        transition: "border-color 0.2s",
      }}
      onFocus={e => e.target.style.borderColor = C.borderHov}
      onBlur={e  => e.target.style.borderColor = C.border}
    />
  );
}

function FieldLabel({ children, required }) {
  return (
    <div style={{
      fontFamily: "'Courier Prime',monospace", fontSize: 9,
      letterSpacing: "0.22em", textTransform: "uppercase",
      color: C.silverDim, marginBottom: 7, display: "flex", alignItems: "center", gap: 4,
    }}>
      {children}
      {required && <span style={{ color: C.red, marginLeft: 2 }}>*</span>}
    </div>
  );
}

// Platform badge (otomatik renk)
function PlatformBadge({ url, size = "sm" }) {
  const p = detectPlatform(url);
  if (!p) return null;
  return (
    <span style={{
      fontFamily: "'Courier Prime',monospace",
      fontSize: size === "lg" ? 9 : 8,
      letterSpacing: "0.18em", textTransform: "uppercase",
      color: p.color, border: `1px solid ${p.color}44`,
      background: `${p.color}0d`,
      borderRadius: 6,
      padding: size === "lg" ? "3px 10px" : "2px 7px",
      lineHeight: 1.5,
    }}>
      {p.icon} {p.name}
    </span>
  );
}

// Tür badge
function TypeBadge({ type }) {
  const t = CONTENT_TYPES.find(x => x.key === type) || CONTENT_TYPES[4];
  return (
    <span style={{
      fontFamily: "'Courier Prime',monospace", fontSize: 8,
      letterSpacing: "0.18em", textTransform: "uppercase",
      color: C.silverDim, border: `1px solid ${C.border}`,
      padding: "2px 7px", lineHeight: 1.4, borderRadius: 6,
    }}>
      {t.icon} {t.label}
    </span>
  );
}

// Gümüş ayırıcı çizgi
function SilverRule({ opacity = 0.08 }) {
  return (
    <div style={{
      height: 1, margin: "4px 0",
      background: `linear-gradient(90deg, transparent, rgba(192,204,224,${opacity}), transparent)`,
    }} />
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// FRAME KARTI
// ═══════════════════════════════════════════════════════════════════════════════

function FrameCard({ frame, onDelete, onBlockUser, isAdmin, currentUid }) {
  const navigate = useNavigate();
  const { t }    = useTranslation();
  const [hov, setHov] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const isOwner  = frame.uid === currentUid;
  const platform = detectPlatform(frame.url || "");
  const liveUser    = useUserInfo(frame.uid);
  const displayName = liveUser.displayName || frame.displayName || t('bonebeat.anonymousUser');
  const photoURL    = liveUser.photoURL    || frame.photoURL    || null;

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: "relative",
        background: C.bgCard,
        backgroundImage: hov
          ? `linear-gradient(rgba(192,204,224,0.02) 1px, transparent 1px),
             linear-gradient(90deg, rgba(192,204,224,0.02) 1px, transparent 1px)`
          : "none",
        backgroundSize: "24px 24px",
        border: `1px solid ${hov ? C.borderHov : C.border}`,
        borderRadius: 8,
        // Üst şerit: platform rengi veya gümüş
        borderTop: `2px solid ${platform ? platform.color + "aa" : (hov ? C.silver : "rgba(192,204,224,0.2)")}`,
        padding: "20px 20px 16px",
        transition: "all 0.3s ease",
        boxShadow: hov
          ? `0 0 28px rgba(192,204,224,0.07), inset 0 0 60px rgba(123,167,212,0.02)`
          : "0 2px 16px rgba(0,0,0,0.6)",
        overflow: "hidden",
        cursor: "default",
      }}
    >
      {/* Köşe ID etiketi */}
      <span style={{
        position: "absolute", bottom: 6, right: 8,
        fontFamily: "'Courier Prime',monospace", fontSize: 7,
        color: "rgba(192,204,224,0.1)", letterSpacing: "0.1em",
        pointerEvents: "none",
        opacity: hov ? 1 : 0, transition: "opacity 0.3s",
      }}>
        [{frame.id?.slice(-4).toUpperCase()}]
      </span>

      {/* ── Üst: başlık + aksiyon butonları ── */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: "'Playfair Display',serif",
            fontSize: "1.05rem", fontWeight: 700,
            color: C.platinum, letterSpacing: "0.04em",
            marginBottom: 7, lineHeight: 1.3,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {frame.title || "İsimsiz İçerik"}
          </div>
          {/* Badge şeridi */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <TypeBadge type={frame.type} />
            {frame.url && <PlatformBadge url={frame.url} />}
          </div>
        </div>

        {/* Aksiyon butonları */}
        <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
          {isAdmin && !isOwner && (
            <IconBtn danger title="Kullanıcıyı Engelle"
              onClick={e => { e.stopPropagation(); onBlockUser(frame.uid, frame.displayName); }}>
              <svg viewBox="0 0 24 24" fill="currentColor" width={13} height={13}>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9C4.63 15.55 4 13.85 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1C19.37 8.45 20 10.15 20 12c0 4.42-3.58 8-8 8z" />
              </svg>
            </IconBtn>
          )}
          {(isAdmin || isOwner) && (
            <IconBtn danger title="Sil"
              onClick={e => { e.stopPropagation(); onDelete(frame.id, frame.title); }}>
              <svg viewBox="0 0 24 24" fill="currentColor" width={13} height={13}>
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zm2.46-7.12l1.41-1.41L12 12.59l2.12-2.12 1.41 1.41L13.41 14l2.12 2.12-1.41 1.41L12 15.41l-2.12 2.12-1.41-1.41L10.59 14l-2.13-2.12zM15.5 4l-1-1h-5l-1 1H5v2h14V4z" />
              </svg>
            </IconBtn>
          )}
        </div>
      </div>

      {/* URL linki */}
      {frame.url && (
        <a href={frame.url} target="_blank" rel="noopener noreferrer"
          style={{
            fontFamily: "'Courier Prime',monospace", fontSize: "0.68rem",
            color: C.silverGhost, letterSpacing: "0.06em",
            textDecoration: "none", display: "block",
            marginBottom: 12, whiteSpace: "nowrap",
            overflow: "hidden", textOverflow: "ellipsis",
            transition: "color 0.2s",
          }}
          onMouseEnter={e => e.currentTarget.style.color = C.silverDim}
          onMouseLeave={e => e.currentTarget.style.color = C.silverGhost}
        >
          ↗ {frame.url}
        </a>
      )}

      {/* Not / Açıklama */}
      {frame.note && (
        <p style={{
          fontFamily: "'Courier Prime',monospace", fontSize: "0.82rem",
          color: C.platinumDim, lineHeight: 1.75,
          marginBottom: 14,
          borderLeft: `2px solid rgba(192,204,224,0.15)`,
          paddingLeft: 10,
          display: "-webkit-box",
          WebkitLineClamp: 4,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}>
          {frame.note}
        </p>
      )}

      {/* ── Alt şerit: Framed by + tarih ── */}
      <SilverRule />
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 6, paddingTop: 8,
      }}>
        {/* Framed by → /profile/:uid */}
        <button
          onClick={e => { e.stopPropagation(); navigate(`/profile/${frame.uid}`); }}
          title={`${displayName} profiline git`}
          style={{
            background: "none", border: "none", padding: 0, cursor: "pointer",
            display: "flex", alignItems: "center", gap: 6, transition: "opacity 0.2s",
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = "0.65"}
          onMouseLeave={e => e.currentTarget.style.opacity = "1"}
        >
          <div style={{
            width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
            background: "rgba(192,204,224,0.08)", border: `1px solid rgba(192,204,224,0.2)`,
            overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {photoURL
              ? <img src={photoURL} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 9, color: C.silverDim }}>
                  {displayName[0].toUpperCase()}
                </span>
            }
          </div>
          <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: "0.65rem", letterSpacing: "0.06em" }}>
            <span style={{ color: C.silverGhost }}>{t('boneframe.framedBy')} </span>
            <span style={{ color: C.silverDim }}>{displayName}</span>
          </span>
        </button>

        <span title={fullDate(frame.createdAt)} style={{
          fontFamily: "'Courier Prime',monospace", fontSize: "0.63rem",
          color: "rgba(192,204,224,0.22)", letterSpacing: "0.06em",
        }}>
          {timeAgo(frame.createdAt)}
        </span>
      </div>

      <button
        type="button"
        onClick={() => setCommentsOpen(open => !open)}
        aria-expanded={commentsOpen}
        aria-controls={`comments-frame-${frame.id}`}
        style={{ marginTop: 10, padding: "6px 10px", border: `1px solid ${C.border}`, borderRadius: 6, background: "transparent", color: C.platinumDim, cursor: "pointer", fontFamily: "'Courier Prime',monospace", fontSize: 10 }}
      >{t(commentsOpen ? "comments.hide" : "comments.show")}</button>
      {commentsOpen && (
        <div id={`comments-frame-${frame.id}`}>
          <CommentSection type="frames" postId={frame.id} />
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// YENİ FRAME FORMU
// ═══════════════════════════════════════════════════════════════════════════════

function NewFrameForm({ currentUser, currentUserData, onSuccess }) {
  const { t } = useTranslation();
  const [title,       setTitle]       = useState("");
  const [url,         setUrl]         = useState("");
  const [note,        setNote]        = useState("");
  const [type,        setType]        = useState("film");
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState("");

  const urlResult = url.trim() ? cleanContentUrl(url) : null;
  const detectedPlatform = urlResult?.isValid ? detectPlatform(urlResult.url) : null;

  // Desteklenen platformlar için grid (BoneBeat yaklaşımı)
  const PLATFORM_GRID = [
    { name: "Netflix",     color: "#e50914" }, { name: "Prime Video",  color: "#00a8e0" },
    { name: "Disney+",     color: "#113ccf" }, { name: "Apple TV+",    color: "#c8c8c8" },
    { name: "HBO Max",     color: "#6a5acd" }, { name: "Hulu",         color: "#1ce783" },
    { name: "Paramount+",  color: "#0064ff" }, { name: "Mubi",         color: "#ff5a5f" },
    { name: "Letterboxd",  color: "#c8c8c8" }, { name: "IMDb",         color: "#f5c518" },
    { name: "Crunchyroll", color: "#f47521" }, { name: "BluTV",        color: "#0055ff" },
    { name: "Gain",        color: "#ff0050" }, { name: "Tabii",        color: "#c0a040" },
    { name: "YouTube",     color: "#ff0000" }, { name: "Vimeo",        color: "#1ab7ea" },
    { name: "JustWatch",   color: "#ffd700" }, { name: "Trakt",        color: "#ed2224" },
  ];

  // Platform ikonunu bul
  function getPlatformIcon(name) {
    return PLATFORMS.find(p => p.name === name)?.icon || "◈";
  }

  async function handleSubmit() {
    if (!title.trim()) { setError(t('boneframe.form.titleRequired')); return; }

    const urlResult = url.trim() ? cleanContentUrl(url) : { isValid: true, url: "", error: null };
    if (!urlResult.isValid) {
      setError(urlResult.error);
      return;
    }

    if (!currentUser?.uid) {
      setError(t('boneframe.form.noSession'));
      console.error("[BoneFrame] handleSubmit: currentUser veya uid yok.", currentUser);
      return;
    }

    setSaving(true); setError("");

    try {
      const payload = {
        title:       filterText(title.trim()),
        url:         urlResult.url,
        note:        filterText(note.trim()),
        type,
        uid:         currentUser.uid,
        displayName: currentUser.displayName || currentUser.email || "Gezgin",
        photoURL:    currentUserData?.photoURL || currentUser.photoURL    || "",
        createdAt:   Date.now(),
      };

      console.log("[BoneFrame] Yeni frame oluşturuluyor:", payload);
      await push(ref(db, "frames"), payload);
      console.log("[BoneFrame] Frame başarıyla kaydedildi.");

      setTitle(""); setUrl(""); setNote(""); setType("film");
      onSuccess();

    } catch (err) {
      console.error("[BoneFrame] Frame oluşturma hatası:", {
        code: err.code, message: err.message, uid: currentUser?.uid, path: "frames",
      });
      if (err.code === "PERMISSION_DENIED") {
        setError(t('boneframe.form.permissionError'));
      } else if (err.message?.includes("network")) {
        setError(t('boneframe.form.networkError'));
      } else {
        setError(`${t('boneframe.form.error')} (${err.code || "bilinmeyen hata"})`);
      }
    }
    setSaving(false);
  }

  return (
    <div style={{ maxWidth: 640 }}>

      {/* Başlık */}
      <p style={{
        fontFamily: "'Courier Prime',monospace", fontSize: 9,
        letterSpacing: "0.38em", color: C.silverGhost,
        textTransform: "uppercase", marginBottom: 6,
      }}>◈ {t('boneframe.form.eyebrow')} ◈</p>
      <h1 style={{
        fontFamily: "'Playfair Display',serif", color: C.silver,
        fontSize: 28, fontWeight: 700, marginBottom: 8,
      }}>
        {t('boneframe.form.title')}
      </h1>
      <p style={{
        fontFamily: "'Courier Prime',monospace", fontSize: 11,
        color: C.silverGhost, letterSpacing: "0.04em", marginBottom: 32, lineHeight: 1.7,
      }}>
        {t('boneframe.form.subtitle')}
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>

        {/* İçerik türü seçimi */}
        <div>
          <FieldLabel>{t('boneframe.form.typeLabel')}</FieldLabel>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {CONTENT_TYPES.map(ct => (
              <button key={ct.key} onClick={() => setType(ct.key)}
                style={{
                  fontFamily: "'Courier Prime',monospace", fontSize: 10,
                  letterSpacing: "0.14em", textTransform: "uppercase",
                  padding: "8px 14px", borderRadius: 6, cursor: "pointer", transition: "all 0.2s",
                  background: type === ct.key ? "rgba(192,204,224,0.1)" : "transparent",
                  border: `1px solid ${type === ct.key ? C.silver : C.border}`,
                  color: type === ct.key ? C.silver : C.silverDim,
                }}>
                {ct.icon} {t(`boneframe.types.${ct.key}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Başlık */}
        <div>
          <FieldLabel required>{t('boneframe.form.titleLabel')}</FieldLabel>
          <TextInput
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder={t('boneframe.form.titlePlaceholder')}
          />
        </div>

        {/* Link */}
        <div>
          <FieldLabel>{t('boneframe.form.urlLabel')}</FieldLabel>
          <TextInput
            value={url}
            onChange={e => { setUrl(e.target.value); setError(""); }}
            placeholder={t('boneframe.form.urlPlaceholder')}
          />
          {url && !detectedPlatform && (
            <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: C.silverGhost, margin: "5px 0 0", letterSpacing: "0.04em" }}>
              {t('boneframe.form.unknownPlatform')}
            </p>
          )}
          {url && detectedPlatform && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 7 }}>
              <PlatformBadge url={url} size="lg" />
              <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: C.green, letterSpacing: "0.06em" }}>
                ✓ {t('boneframe.form.detected')}
              </span>
            </div>
          )}
        </div>

        {/* Not (isteğe bağlı) */}
        <div>
          <FieldLabel>{t('boneframe.form.noteLabel')}</FieldLabel>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder={t('boneframe.form.notePlaceholder')}
            rows={4}
            style={{
              display: "block", width: "100%", boxSizing: "border-box",
              padding: "10px 13px", borderRadius: 6, resize: "vertical",
              background: C.bgInput, border: `1px solid ${C.border}`,
              color: C.platinum, fontFamily: "'Courier Prime',monospace",
              fontSize: 12, outline: "none", letterSpacing: "0.04em",
              lineHeight: 1.75, transition: "border-color 0.2s",
            }}
            onFocus={e => e.target.style.borderColor = C.borderHov}
            onBlur={e  => e.target.style.borderColor = C.border}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
            <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: C.silverGhost, letterSpacing: "0.08em" }}>
              {note.length} {t('boneframe.form.chars')}
            </span>
          </div>
        </div>

        {/* Desteklenen platformlar */}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <span style={{
              fontFamily: "'Courier Prime',monospace", fontSize: 9,
              letterSpacing: "0.24em", color: "rgba(192,204,224,0.2)",
              textTransform: "uppercase",
            }}>{t('boneframe.form.supportedPlatforms')}</span>
            <div style={{ flex: 1, height: 1, background: C.borderFaint }} />
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
            {PLATFORM_GRID.map(p => (
              <span key={p.name} style={{
                fontFamily: "'Courier Prime',monospace", fontSize: 9,
                letterSpacing: "0.1em", padding: "3px 9px", borderRadius: 6,
                border: `1px solid ${p.color}22`,
                color: `${p.color}bb`,
                background: `${p.color}0d`,
              }}>
                {getPlatformIcon(p.name)} {p.name}
              </span>
            ))}
          </div>
        </div>

        {/* Hata mesajı */}
        {error && (
          <p style={{
            fontFamily: "'Courier Prime',monospace", fontSize: 11,
            color: C.red, letterSpacing: "0.04em", lineHeight: 1.6,
          }}>⚠ {error}</p>
        )}

        {/* Gönder butonu */}
        <div>
          <PrimaryBtn onClick={handleSubmit} disabled={!title.trim() || saving}>
            {saving ? `◈ ${t('boneframe.form.saving')}` : `◈ ${t('boneframe.form.submit')}`}
          </PrimaryBtn>
        </div>

      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MODERASYON PANELİ
// ═══════════════════════════════════════════════════════════════════════════════

function ModerationPanel({ frames, onDelete, onBlockUser }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const filtered = frames.filter(f =>
    (f.title       || "").toLowerCase().includes(search.toLowerCase()) ||
    (f.displayName || "").toLowerCase().includes(search.toLowerCase()) ||
    (f.uid         || "").includes(search)
  );

  return (
    <div>
      <p style={{
        fontFamily: "'Courier Prime',monospace", fontSize: 9,
        letterSpacing: "0.38em", color: "rgba(224,92,110,0.4)",
        textTransform: "uppercase", marginBottom: 6,
      }}>◈ Admin ◈</p>
      <h1 style={{
        fontFamily: "'Playfair Display',serif", color: C.red,
        fontSize: 28, fontWeight: 700, marginBottom: 8,
      }}>{t('bonebeat.mod.title')}</h1>
      <SilverRule />

      {/* İstatistikler */}
      <div style={{ display: "flex", gap: 12, margin: "20px 0 20px", flexWrap: "wrap" }}>
        {[
          { label: t('boneframe.mod.totalFrames'),   val: frames.length },
          { label: t('bonebeat.mod.uniqueUsers'),     val: new Set(frames.map(f => f.uid)).size },
          { label: t('boneframe.mod.platformCount'),  val: new Set(frames.map(f => detectPlatform(f.url)?.name).filter(Boolean)).size },
        ].map(stat => (
          <div key={stat.label} style={{
            background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 8,
            padding: "14px 20px", flex: 1, minWidth: 120,
          }}>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 700, color: C.silver }}>{stat.val}</div>
            <div style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: C.silverGhost, letterSpacing: "0.15em", textTransform: "uppercase", marginTop: 2 }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Arama */}
      <div style={{ marginBottom: 16 }}>
        <TextInput
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('boneframe.mod.searchPlaceholder')}
        />
      </div>

      {/* Tablo */}
      <div style={{ background: C.bgDeep, border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
        <div className="sf-mod-row" style={{ padding: "10px 16px", borderBottom: `1px solid ${C.border}` }}>
          {[t('boneframe.mod.colTitle'), t('bonebeat.mod.colBy'), t('boneframe.mod.colPlatform'), t('bonebeat.mod.colAction')].map(h => (
            <span key={h} style={{
              fontFamily: "'Courier Prime',monospace", fontSize: 8,
              color: C.silverGhost, letterSpacing: "0.25em", textTransform: "uppercase",
            }}>{h}</span>
          ))}
        </div>

        {filtered.length === 0 && (
          <div style={{ padding: "24px", textAlign: "center", fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.silverGhost }}>
            {t('bonebeat.mod.noResults')}
          </div>
        )}

        {filtered.map((f, i) => {
          const p = detectPlatform(f.url || "");
          return (
            <div key={f.id} className="sf-mod-row" style={{
              padding: "12px 16px", alignItems: "center",
              borderBottom: i < filtered.length - 1 ? `1px solid ${C.borderFaint}` : "none",
            }}>
              <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 12, color: C.platinum, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {f.title || "—"}
              </span>
              <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.silverDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {f.displayName || "—"}
              </span>
              <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 10, color: p?.color || C.silverGhost }}>
                {p ? `${p.icon} ${p.name}` : "—"}
              </span>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                <GhostBtn onClick={() => onBlockUser(f.uid, f.displayName)} danger>{t('admin.block')}</GhostBtn>
                <GhostBtn onClick={() => onDelete(f.id, f.title)} danger>{t('common.delete')}</GhostBtn>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ANA MODÜL — BoneFrameInner
// ═══════════════════════════════════════════════════════════════════════════════

function BoneFrameInner() {
  const { user, userData, isAdmin: ctxAdmin, authLoading = false } = useAuth();
  const { t } = useTranslation();
  const isAdmin = ctxAdmin || user?.isAdmin || false;

  const [frames,        setFrames]        = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [dbError,       setDbError]       = useState(null);
  const [view,          setView]          = useState("feed");
  const [toast,         setToast]         = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [blockConfirm,  setBlockConfirm]  = useState(null);

  // ── Firebase listener (BoneBeat ile aynı authLoading koruması) ─────────────
  useEffect(() => {
    if (authLoading) return;

    setLoading(true);
    setDbError(null);

    const unsub = onValue(
      ref(db, "frames"),
      (snap) => {
        if (snap.exists()) {
          const list = Object.entries(snap.val())
            .map(([id, v]) => ({ id, ...v }))
            .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
          setFrames(list);
        } else {
          setFrames([]);
        }
        setLoading(false);
      },
      (err) => {
        console.error("[BoneFrame] Firebase okuma hatası:", err.code, err.message);
        setDbError(err.message);
        setFrames([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [authLoading]);

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(""), 2800); }

  async function handleDelete(id) {
    try {
      await remove(ref(db, `frames/${id}`));
      console.log("[BoneFrame] Frame silindi:", id);
      showToast(t('boneframe.toast.deleted'));
    } catch (err) {
      console.error("[BoneFrame] Silme hatası:", err.code, err.message);
      showToast(t('boneframe.toast.deleteFailed'));
    }
    setConfirmDelete(null);
  }

  async function handleBlockUser(uid, displayName) {
    try {
      await update(ref(db, `users/${uid}`), { status: "blocked" });
      showToast(`${displayName || uid} ${t('bonebeat.toast.blocked')}`);
    } catch (err) {
      console.error("[BoneFrame] Engelleme hatası:", err.code, err.message);
      showToast(t('bonebeat.toast.blockFailed'));
    }
    setBlockConfirm(null);
  }

  const visibleFrames  = view === "mine" ? frames.filter(f => f.uid === user?.uid) : frames;
  const isPageLoading  = authLoading || loading;

  const feedMeta = {
    feed: { pre: t('boneframe.archiveEyebrow'), title: t('boneframe.archiveTitle') },
    mine: { pre: t('boneframe.myEyebrow'),      title: t('boneframe.myTitle') },
  };

  return (
    <div style={{ minHeight: "100vh", background: C.bg }}>
      <SidebarFrame activeView={view} onNavigate={setView} isAdmin={isAdmin} />

      <main className="sf-main bf-main" style={{ padding: "40px clamp(20px,4vw,60px)", minHeight: "100vh", overflowY: "auto" }}>

        {/* Toast */}
        {toast && (
          <div style={{
            position: "fixed", bottom: 28, right: 28, zIndex: 9999,
            background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 6,
            padding: "12px 20px", fontFamily: "'Courier Prime',monospace",
            fontSize: 11, color: C.platinum, boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
            letterSpacing: "0.06em",
          }}>
            {toast}
          </div>
        )}

        {/* Silme onayı */}
        {confirmDelete && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.82)", zIndex: 9998, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 8, padding: "32px 36px", maxWidth: 360, textAlign: "center" }}>
              <p style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, color: C.platinum, marginBottom: 8 }}>{t('bonebeat.confirm.sure')}</p>
              <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.silverDim, marginBottom: 24 }}>
                "<strong>{confirmDelete.name}</strong>" {t('bonebeat.confirm.deleteBody')}
              </p>
              <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
                <GhostBtn onClick={() => setConfirmDelete(null)}>{t('common.cancel')}</GhostBtn>
                <GhostBtn onClick={() => handleDelete(confirmDelete.id)} danger>{t('common.delete')}</GhostBtn>
              </div>
            </div>
          </div>
        )}

        {/* Engelleme onayı */}
        {blockConfirm && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.82)", zIndex: 9998, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 8, padding: "32px 36px", maxWidth: 360, textAlign: "center" }}>
              <p style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, color: C.red, marginBottom: 8 }}>{t('bonebeat.confirm.blockTitle')}</p>
              <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.silverDim, marginBottom: 24 }}>
                <strong>{blockConfirm.name}</strong> {t('bonebeat.confirm.blockBody')}
              </p>
              <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
                <GhostBtn onClick={() => setBlockConfirm(null)}>{t('common.cancel')}</GhostBtn>
                <GhostBtn onClick={() => handleBlockUser(blockConfirm.uid, blockConfirm.name)} danger>{t('admin.block')}</GhostBtn>
              </div>
            </div>
          </div>
        )}

        {/* ── View Routing ── */}
        {view === "new" ? (
          <NewFrameForm currentUser={user} currentUserData={userData} onSuccess={() => setView("feed")} />

        ) : view === "moderation" && isAdmin ? (
          <ModerationPanel
            frames={frames}
            onDelete={(id, name) => setConfirmDelete({ id, name })}
            onBlockUser={(uid, name) => setBlockConfirm({ uid, name })}
          />

        ) : (
          <>
            {/* Sayfa başlığı */}
            <div style={{ marginBottom: 32 }}>
              <p style={{
                fontFamily: "'Courier Prime',monospace", fontSize: 9,
                letterSpacing: "0.38em", color: C.silverGhost,
                textTransform: "uppercase", marginBottom: 6,
              }}>
                {feedMeta[view]?.pre || "◈ Film Arşivi ◈"}
              </p>
              <h1 style={{ fontFamily: "'Playfair Display',serif", color: C.silver, fontSize: 30, fontWeight: 700, margin: 0 }}>
                {feedMeta[view]?.title || "BoneFrame Arşivi"}
              </h1>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
                <div style={{ height: 1, flex: 1, background: C.border }} />
                <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: C.silverGhost, letterSpacing: "0.2em" }}>
                  {isPageLoading ? "…" : `${visibleFrames.length} içerik`}
                </span>
              </div>
            </div>

            {/* Yükleniyor */}
            {isPageLoading ? (
              <div style={{ textAlign: "center", padding: "80px 0" }}>
                <div style={{
                  width: 36, height: 36, borderRadius: "50%", margin: "0 auto 20px",
                  border: "2px solid rgba(192,204,224,0.12)",
                  borderTopColor: "rgba(192,204,224,0.5)",
                  animation: "bfr-spin 0.9s linear infinite",
                }} />
                <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.silverGhost, letterSpacing: "0.2em", marginBottom: 6 }}>
                  {t('boneframe.loadingArchive')}
                </p>
                <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: "rgba(192,204,224,0.15)", letterSpacing: "0.14em" }}>
                  {t('boneframe.loadingHint')}
                </p>
                <style>{`@keyframes bfr-spin { to { transform: rotate(360deg); } }`}</style>
              </div>

            ) : dbError ? (
              /* Firebase okuma hatası */
              <div style={{
                maxWidth: 480, margin: "60px auto",
                background: C.bgCard, border: `1px solid rgba(224,92,110,0.2)`,
                padding: "40px 36px", textAlign: "center",
              }}>
                <div style={{ fontSize: 32, marginBottom: 16, opacity: 0.5 }}>⚠</div>
                <p style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, color: C.red, marginBottom: 10, fontWeight: 700 }}>
                  {t('bonebeat.errorTitle')}
                </p>
                <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 10, color: "rgba(224,92,110,0.5)", marginBottom: 24, lineHeight: 1.7, letterSpacing: "0.04em", wordBreak: "break-word" }}>
                  {dbError}
                </p>
                <GhostBtn onClick={() => window.location.reload()}>{t('bonebeat.reload')}</GhostBtn>
              </div>

            ) : visibleFrames.length === 0 ? (

              /* Boş durum */
              view === "mine" ? (
                <div style={{
                  maxWidth: 480, margin: "60px auto",
                  background: C.bgCard, border: `1px solid ${C.border}`,
                  padding: "48px 40px", textAlign: "center",
                }}>
                  <div style={{ marginBottom: 24 }}>
                    <svg viewBox="0 0 24 24" fill="none" width={40} height={40} style={{ margin: "0 auto", display: "block", opacity: 0.3 }}>
                      <rect x="2" y="3" width="20" height="18" rx="2" stroke={C.silver} strokeWidth="1" />
                      <path d="M10 8l6 4-6 4V8z" stroke={C.silver} strokeWidth="1.2" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <p style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, color: C.silverDim, marginBottom: 10, fontWeight: 700 }}>
                    {t('boneframe.noFrames')}
                  </p>
                  <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.silverGhost, marginBottom: 28, lineHeight: 1.8, letterSpacing: "0.04em" }}>
                    {t('boneframe.noFramesHint')}
                  </p>
                  <PrimaryBtn onClick={() => setView("new")}>◈ {t('boneframe.addFrame')}</PrimaryBtn>
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "80px 0" }}>
                  <p style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, color: C.silverDim, marginBottom: 8 }}>
                    {t('boneframe.archiveEmpty')}
                  </p>
                  <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.silverGhost, marginBottom: 20 }}>
                    {t('boneframe.archiveEmptyHint')}
                  </p>
                  <PrimaryBtn onClick={() => setView("new")}>◈ {t('boneframe.addFrame')}</PrimaryBtn>
                </div>
              )

            ) : (
              /* İçerik grid */
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
                {visibleFrames.map(f => (
                  <FrameCard
                    key={f.id} frame={f}
                    onDelete={(id, name) => setConfirmDelete({ id, name })}
                    onBlockUser={(uid, name) => setBlockConfirm({ uid, name })}
                    isAdmin={isAdmin} currentUid={user?.uid}
                  />
                ))}
              </div>
            )}
          </>
        )}

      </main>

      <style>{`
        ::-webkit-scrollbar { width: 4px; background: ${C.bgDeep}; }
        ::-webkit-scrollbar-thumb { background: rgba(192,204,224,0.1); }
        ::selection { background: rgba(192,204,224,0.15); color: ${C.platinum}; }

        .sf-mod-row {
          display: grid;
          grid-template-columns: minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr) 130px;
          gap: 8px 12px;
        }
        @media (max-width: 640px) {
          .sf-mod-row { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
          .sf-mod-row > :first-child,
          .sf-mod-row > :nth-child(4) { grid-column: 1 / -1; }
        }

        @media (max-width: 768px) {
          .bf-main {
            margin-left: 0 !important;
            width: 100% !important;
            padding-top: 64px !important;
          }
        }
      `}</style>
    </div>
  );
}

// ── Dışa aktarım — SidebarFrameProvider sarmalı ───────────────────────────────
export default function BoneFrame() {
  return (
    <SidebarFrameProvider>
      <BoneFrameInner />
    </SidebarFrameProvider>
  );
}
